import React, { useState, useRef } from "react";
import { Box, Typography, IconButton, FormControl, Select, MenuItem, Tooltip, tooltipClasses } from "@mui/material";
import { styled } from '@mui/material/styles';
import { Document, Page, pdfjs } from "react-pdf";
import {
  Download,
  Fullscreen,
  FullscreenExit,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import rightIcon from "../icon/verifiedGreen.png";

// Stable worker initialization via CDN (works across CRA/webpack/vite)
(() => {
  try {
    const ver = (pdfjs.version || "4.0.0").replace(/[^0-9.]/g, "");
    const major = Number((ver.split('.')[0]) || 0);
    const ext = major >= 5 ? 'mjs' : 'js';
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${ver}/pdf.worker.min.${ext}`;
  } catch (_) {}
})();

// const ZOOM_OPTIONS = [
//   { label: "50%", value: 0.5 },
//   { label: "75%", value: 0.75 },
//   { label: "100%", value: 1 },
//   { label: "125%", value: 1.25 },
//   { label: "150%", value: 1.5 },
//   { label: "200%", value: 2 },
//   { label: "Fit Width", value: "fit" },
// ];

const DocTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#034A7F',
    color: '#ffffff',
    fontSize: '0.8rem',
    fontWeight: 500,
    fontFamily: '"Nunito Sans", "Roboto", "Arial", sans-serif',
    borderRadius: '8px',
    padding: '8px 14px',
    maxWidth: 500,
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    letterSpacing: '0.01em',
    lineHeight: 1.5,
    wordBreak: 'break-word',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#1a1a2e',
  },
}));

const DocumentPreview = ({ currentPage, file, setTotalPages, title, verificationId }) => {
  const [numPages, setNumPages] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const viewerRef = useRef(null);

  // Reset numPages when file changes to avoid stale rendering
 React.useEffect(() => {
  console.log(' DocumentPreview Debug:', {
    verificationId,
    type: typeof verificationId,
    isVerified: verificationId === 1 || verificationId === "1",
    title
  });
}, [verificationId, title]);
// NEW CODE:
const isVerified = verificationId === 1 ||
                   verificationId === "1" ||
                   verificationId === true ||
                   verificationId === "true";

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(typeof numPages === 'number' && numPages > 0 ? numPages : 0);
    if (setTotalPages) setTotalPages(numPages || 0);
  };

  const onDocumentLoadError = (err) => {
    // Keep UI from crashing; prevent Page from trying to render
    // eslint-disable-next-line no-console
    console.error("PDF load error:", err?.message || err);
    setNumPages(0);
    if (setTotalPages) setTotalPages(0);
  };

  const fileName = React.useMemo(() => {
    if (typeof file !== 'string' || !file) return 'document.pdf';
    try {
      // Prefer URL API and decodeURIComponent for robust parsing
      const u = new URL(file);
      const last = u.pathname.split('/').pop();
      return decodeURIComponent(last || 'document.pdf');
    } catch {
      try {
        const clean = file.split('?')[0];
        return decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1) || 'document.pdf');
      } catch {
        return 'document.pdf';
      }
    }
  }, [file]);

  const downloadName = (title && typeof title === 'string' && title.trim()) || fileName;

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = file;
    link.download = downloadName;
    link.click();
  };

  //  Ctrl + Scroll zoom with cursor anchor
  const handleWheel = (e) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const container = viewerRef.current;
      if (!container) return;

      const { left, top } = container.getBoundingClientRect();
      const offsetX = e.clientX - left + container.scrollLeft;
      const offsetY = e.clientY - top + container.scrollTop;

      const oldScale = scale;
      let newScale = scale;

      if (e.deltaY < 0) newScale = Math.min(scale + 0.1, 3);
      else newScale = Math.max(scale - 0.1, 0.5);

      setScale(newScale);

      const scaleRatio = newScale / oldScale;
      container.scrollLeft = offsetX * scaleRatio - (e.clientX - left);
      container.scrollTop = offsetY * scaleRatio - (e.clientY - top);
    }
  };

  //  Dragging only when zoom > 1
  const handleMouseDown = (e) => {
    if (scale <= 1 || !viewerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - viewerRef.current.offsetLeft);
    setStartY(e.pageY - viewerRef.current.offsetTop);
    setScrollLeft(viewerRef.current.scrollLeft);
    setScrollTop(viewerRef.current.scrollTop);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1 || !viewerRef.current) return;
    e.preventDefault();
    const x = e.pageX - viewerRef.current.offsetLeft;
    const y = e.pageY - viewerRef.current.offsetTop;
    viewerRef.current.scrollLeft = scrollLeft - (x - startX);
    viewerRef.current.scrollTop = scrollTop - (y - startY);
  };

  const handleMouseUp = () => setIsDragging(false);

  // Dropdown change handler
  const handleZoomChange = (value) => {
    if (value === "fit") {
      // Fit width inside viewer
      const container = viewerRef.current;
      if (container) {
        const pdfWidth = container.clientWidth - 32; // account for padding
        const approxPdfOriginalWidth = 600; // adjust based on your doc
        const fitScale = pdfWidth / approxPdfOriginalWidth;
        setScale(fitScale);
      }
    } else {
      setScale(value);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        backgroundColor: "white",
        borderRadius: 2,
        overflow: "hidden",   // keep box fixed
        boxShadow: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,          // prevents pushing ExtractedData
        ...(fullscreen && {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2000,
          borderRadius: 0,
        }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
         <Box
  sx={{
    display: "flex",
    alignItems: "center",
    maxWidth: "60%",
    overflow: "hidden",
  }}
>
 <DocTooltip
  title={(title && typeof title === "string" && title) || fileName || "Document Preview"}
  arrow
  placement="bottom-start"
  enterDelay={200}
  enterNextDelay={100}
>
  <Typography
    variant="h6"
    sx={{
      fontWeight: 600,
      color: "#2e4258",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      cursor: "default",
    }}
  >
    {(title && typeof title === "string" && title) ||
      fileName ||
      "Document Preview"}
  </Typography>
</DocTooltip>
 
  {/* Icon right behind the text */}
  {isVerified && (
  <img
    src={rightIcon}
    alt="verified"
    style={{
      width: 20,
      height: 20,
      marginLeft: 8,
      flexShrink: 0,
    }}
  />
)}
</Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Zoom Select */}
          {/* <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={ZOOM_OPTIONS.find(opt => opt.value === scale)?.value || "custom"}
              onChange={(e) => handleZoomChange(e.target.value)}
              sx={{
                "& .MuiSelect-select": { py: 0.5, px: 1.5, fontSize: 14 },
              }}
            >
              {ZOOM_OPTIONS.map((opt) => (
                <MenuItem key={opt.label} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}

          <IconButton onClick={() => setScale((s) => Math.min(s + 0.2, 3))}>
            <ZoomIn sx={{ color: "#2e4258" }} />
          </IconButton>
          <IconButton onClick={() => setScale((s) => Math.max(s - 0.2, 0.5))}>
            <ZoomOut sx={{ color: "#2e4258" }} />
          </IconButton>
          <IconButton onClick={handleDownload}>
            <Download sx={{ color: "#2e4258" }} />
          </IconButton>
          <IconButton onClick={() => setFullscreen(!fullscreen)}>
            {fullscreen ? (
              <FullscreenExit sx={{ color: "#2e4258" }} />
            ) : (
              <Fullscreen sx={{ color: "#2e4258" }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* PDF Viewer */}
      <Box
        ref={viewerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        sx={{
          flex: 1,
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          backgroundColor: "#fafafa",
          overflow: "auto",
          minHeight: 0,
          cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
        }}
      >
        {file ? (
          // Inner zoom wrapper (keeps outer box fixed)
          <Box
            sx={{
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              transition: "transform 0.1s ease-out",
            }}
          >
            <Document
              key={typeof file === 'string' ? file : 'doc'}
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
            >
              {typeof numPages === 'number' && numPages > 0 && (
                Array.from({ length: numPages }).map((_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ))
              )}
            </Document>
          </Box>
        ) : (
          // Friendly empty state when there is no PDF URL
          <Box sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            width: '100%'
          }}>
            <Typography>No PDF file specified.</Typography>
          </Box>
        )}
      </Box>

      
    </Box>
  );
};

export default DocumentPreview;
